import {
  createPlugin,
  createRoutableExtension,
  createApiFactory,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { rootRouteRef } from './routes';
import { sreDashboardApiRef } from './api/SreDashboardApi';
import { SreDashboardClient } from './api/SreDashboardClient';

/**
 * SRE Dashboard Plugin
 *
 * Provides the full SRE Dashboard with 8 tabs:
 * Overview, Incidents, Runbooks, SLO, Postmortems, DailyOps, Governance, History
 *
 * Plus standalone pages for:
 * - GovernancePage (/governance)
 * - ResourcesPage (/resources)
 * - DecisionsPage (/decisions)
 */
export const sreDashboardPlugin = createPlugin({
  id: 'sre-dashboard',
  routes: {
    root: rootRouteRef,
  },
  apis: [
    createApiFactory({
      api: sreDashboardApiRef,
      deps: { fetchApi: fetchApiRef },
      factory: ({ fetchApi }) => new SreDashboardClient({ fetchApi }),
    }),
  ],
});

/**
 * Main SRE Dashboard page (8-tab layout, routable at /sre-dashboard/*)
 */
export const SreDashboardPage = sreDashboardPlugin.provide(
  createRoutableExtension({
    name: 'SreDashboardPage',
    component: () =>
      import('./components/SreDashboardPage/SreDashboardPage').then(
        m => m.SreDashboardPage,
      ),
    mountPoint: rootRouteRef,
  }),
);

/**
 * Governance standalone page (routable at /governance/*)
 */
export const GovernancePage = sreDashboardPlugin.provide(
  createRoutableExtension({
    name: 'GovernancePage',
    component: () =>
      import('./components/Governance/GovernancePage').then(m => m.GovernancePage),
    mountPoint: rootRouteRef,
  }),
);

/**
 * Resource Exclusion standalone page (routable at /resources)
 */
export const ResourcesPage = sreDashboardPlugin.provide(
  createRoutableExtension({
    name: 'ResourcesPage',
    component: () =>
      import('./components/Resources/ResourceExclusionPage').then(
        m => m.ResourceExclusionPage,
      ),
    mountPoint: rootRouteRef,
  }),
);

/**
 * Decision History standalone page (routable at /decisions)
 */
export const DecisionsPage = sreDashboardPlugin.provide(
  createRoutableExtension({
    name: 'DecisionsPage',
    component: () =>
      import('./components/History/DecisionHistoryPage').then(
        m => m.DecisionHistoryPage,
      ),
    mountPoint: rootRouteRef,
  }),
);
